import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, Clock } from "lucide-react";
import api, { imgUrl } from "@/lib/api";
import PincodeChecker from "@/components/PincodeChecker";

const IMG = {
  hero: "https://images.unsplash.com/photo-1774366126885-bef99f2f6d81?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  cake: "https://images.unsplash.com/photo-1624802674607-93bedb6cb1aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  flower: "https://images.unsplash.com/photo-1521520368710-3ab197656d60?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  hamper: "https://images.unsplash.com/photo-1608755728617-aefab37d2edd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  mug: "https://images.unsplash.com/photo-1615860291946-62f346ab7e63?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  combo: "https://images.unsplash.com/photo-1522061658684-3edeef4ff7f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  baker: "https://images.unsplash.com/photo-1731576089270-9e806089a40f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  testimonial: "https://images.unsplash.com/photo-1575384043001-f37f48835528?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="relative h-[86vh] min-h-[560px] overflow-hidden">
          <img src={IMG.hero} alt="Signature Cake" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 hero-grad-overlay" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-16 md:pb-24 text-white">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <div className="overline text-white" style={{ color: "#F9F8F6", opacity: 0.9 }}>Handcrafted · Pan-India Delivery</div>
              <h1 className="font-display text-5xl md:text-7xl leading-[1.05] mt-4 max-w-3xl">
                A little slice of joy, <em className="italic font-normal">delivered on time.</em>
              </h1>
              <p className="mt-6 max-w-xl text-white/85 text-lg font-light">
                Freshly baked cakes, hand-tied blooms and curated hampers - sent to their door in the slot you choose.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/shop/cakes" data-testid="hero-shop-cakes" className="btn-primary">Shop Cakes <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/shop/hampers" data-testid="hero-shop-hampers" className="inline-flex items-center gap-2 rounded-full border border-white/90 text-white bg-white/5 px-8 py-3 font-semibold hover:bg-white hover:text-[#2D2A26] transition-colors">Curated Hampers</Link>
              </div>
            </motion.div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto -mt-14 relative z-10 px-6">
          <PincodeChecker />
        </div>
      </section>

      {/* CATEGORY BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">Shop by mood</div>
            <h2 className="font-display text-3xl md:text-5xl mt-2">Every gift, thoughtfully made.</h2>
          </div>
          <Link to="/shop/cakes" className="hidden md:inline text-sm link-underline">Explore all</Link>
        </div>
        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="category-bento">
          <CategoryTile to="/shop/cakes" img={IMG.cake} title="Signature Cakes" tag="From ₹499" className="col-span-12 md:col-span-7 md:row-span-2 h-[420px] md:h-full min-h-[540px]" />
          <CategoryTile to="/shop/combos" img={IMG.combo} title="Gift Combos" tag="From ₹999" className="col-span-6 md:col-span-5 h-[260px]" />
          <CategoryTile to="/shop/flowers" img={IMG.flower} title="Fresh Flowers" tag="From ₹649" className="col-span-6 md:col-span-3 h-[260px]" />
          <CategoryTile to="/shop/hampers" img={IMG.hamper} title="Gift Hampers" tag="From ₹1,299" className="col-span-6 md:col-span-2 h-[260px]" />
        </div>
        <div className="mt-6 text-center">
          <Link to="/shop/mugs" className="text-sm link-underline text-[#6C665F]">or browse our coffee mugs collection →</Link>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        <div className="mb-10">
          <div className="overline">Bestsellers</div>
          <h2 className="font-display text-3xl md:text-5xl mt-2">This week's most-gifted.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 md:gap-8" data-testid="home-featured-grid">
          {featured.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link to={`/product/${p.id}`} className="group block" data-testid={`home-product-${p.id}`}>
                <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-[#F4F2EC]">
                  <img src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover product-img" />
                </div>
                <div className="mt-4 flex justify-between items-start">
                  <div>
                    <div className="overline">{p.category}</div>
                    <div className="font-display text-lg md:text-xl mt-1">{p.name}</div>
                  </div>
                  <div className="font-semibold">₹{p.price}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROMISE STRIP */}
      <section className="bg-[#2D2A26] text-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Promise icon={<Truck className="w-5 h-5" />} title="Pan-India Delivery" text="Serviceable across major cities. Add more via admin." />
          <Promise icon={<Clock className="w-5 h-5" />} title="Choose Your Slot" text="Morning, evening or midnight surprise - your call." />
          <Promise icon={<ShieldCheck className="w-5 h-5" />} title="UPI, Cards & COD" text="Secure Razorpay checkout or Cash on Delivery." />
        </div>
      </section>

      {/* OCCASIONS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="overline">For every celebration</div>
            <h2 className="font-display text-3xl md:text-5xl mt-2">Shop the occasion.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="home-occasions">
          <OccasionCard slug="birthday" title="Birthday" img="https://images.unsplash.com/photo-1558636508-e0db3814bd1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" />
          <OccasionCard slug="anniversary" title="Anniversary" img="https://images.unsplash.com/photo-1509937528035-ad76254b0356?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" />
          <OccasionCard slug="valentine" title="Valentine's" img="https://images.unsplash.com/photo-1518895949257-7621c3c786d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" />
          <OccasionCard slug="rakhi" title="Raksha Bandhan" img="https://images.unsplash.com/photo-1470309864661-68328b2cd0a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" />
        </div>
      </section>

      {/* ABOUT SPLIT */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
        <div>
          <div className="overline">Our story</div>
          <h2 className="font-display text-3xl md:text-5xl mt-2">Small kitchen, <em className="italic font-normal">big love.</em></h2>
          <p className="mt-6 text-[#6C665F] leading-relaxed">
            Eko started in a home kitchen with a stubborn belief - that gifting should feel personal, taste incredible, and arrive on time. Today we bake, arrange, and pack each order by hand, and hand it to a courier who understands "please don't tilt the cake".
          </p>
          <Link to="/shop/cakes" className="btn-primary mt-8">Start Gifting <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="aspect-[4/5] rounded-3xl overflow-hidden">
          <img src={IMG.baker} alt="Baker" className="w-full h-full object-cover" />
        </div>
      </section>
    </div>
  );
}

function CategoryTile({ to, img, title, tag, className }) {
  return (
    <Link to={to} className={`relative overflow-hidden rounded-2xl group ${className}`} data-testid={`category-${title.toLowerCase().split(" ")[0]}`}>
      <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover product-img" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <div className="absolute bottom-5 left-5 right-5 text-white">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-white/90">{tag}</div>
        <div className="font-display text-2xl md:text-3xl mt-1">{title}</div>
      </div>
    </Link>
  );
}

function Promise({ icon, title, text }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-full bg-[#8C3A3A] flex items-center justify-center shrink-0">{icon}</div>
      <div>
        <div className="font-display text-xl">{title}</div>
        <div className="text-sm text-white/70 mt-1">{text}</div>
      </div>
    </div>
  );
}

function OccasionCard({ slug, title, img }) {
  return (
    <Link to={`/occasions/${slug}`} className="group relative block aspect-[4/5] rounded-2xl overflow-hidden" data-testid={`home-occasion-${slug}`}>
      <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover product-img" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute bottom-4 left-4 right-4 text-white">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-white/85">Shop for</div>
        <div className="font-display text-2xl mt-1">{title}</div>
      </div>
    </Link>
  );
}
